import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useAuth } from "../../contexts/AuthContext"
import Header from "../layout/Header"
import { connectionGuides } from "./connectionGuides"

const examples = [
  ["Plan your week", "Use Heartily to list assignments due this week. Suggest a study plan, and ask me how much time I have."],
  ["Find what needs attention", "Show my overdue assignments and their submission status. Include the course and due date."],
  ["Understand your grades", "Summarize the available grades for my recent assignments. Tell me which ones are still ungraded."],
  ["Check in as a parent", "List my linked students. For the student I choose, summarize upcoming assignments and anything marked missing."]
]

export default function AiAssistantsPage() {
  const { isAuthenticated } = useAuth()
  const [selected, setSelected] = useState("claude")
  const [copyStatus, setCopyStatus] = useState("")
  const guide = connectionGuides.find((item) => item.id === selected)
  const serverUrl = document.querySelector('meta[name="mcp-server-url"]')?.content || "https://heartily.app/mcp"
  const isLocal = /^http:\/\/(localhost|127\.0\.0\.1)(:|\/)/.test(serverUrl)

  useEffect(() => {
    const previousTitle = document.title
    document.title = "AI assistants & connection guides — Heartily"
    if (!window.location.hash) window.scrollTo(0, 0)
    return () => { document.title = previousTitle }
  }, [])

  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(serverUrl)
      setCopyStatus("Server address copied.")
    } catch {
      setCopyStatus("Select the address above and copy it manually.")
    }
  }

  return (
    <div className="ai-guide">
      {isAuthenticated ? <Header /> : (
        <header className="ai-public-header">
          <nav aria-label="Main navigation" className="ai-container ai-public-nav">
            <Link to="/" aria-label="Heartily homepage"><img src="/images/heartilylogo.png" alt="Heartily" /></Link>
            <div><Link to="/welcome">Explore Heartily</Link><a href="/users/sign_in">Sign in</a></div>
          </nav>
        </header>
      )}
      <main>
        <section className="ai-hero">
          <div className="ai-container">
            <p className="ai-eyebrow">Heartily + your AI assistant</p>
            <h1>Your school week.<br />A conversation away.</h1>
            <p className="ai-lead">Bring your courses, deadlines and grades into the assistant you already use. Ask better questions with your actual school data.</p>
            <div className="ai-hero-actions"><a className="ai-button ai-button-primary" href="#connect">Connect your assistant</a>{isAuthenticated ? <a href="/oauth/authorized_applications">Manage connected apps</a> : <a href="/users/sign_up">Create a free Heartily account</a>}</div>
            <p className="ai-provider-line">Claude · ChatGPT · Grok · Grok Bot · compatible MCP clients</p>
          </div>
        </section>

        <section className="ai-section ai-white" aria-labelledby="possibilities">
          <div className="ai-container">
            <p className="ai-eyebrow">Less searching. More doing.</p>
            <h2 id="possibilities">Start with a question.</h2>
            <p className="ai-section-intro">Heartily supplies the school data. Your assistant helps you make sense of it.</p>
            <dl className="ai-example-grid">{examples.map(([title, prompt]) => <div key={title}><dt>{title}</dt><dd>“{prompt}”</dd></div>)}</dl>
            <p className="ai-muted">Examples are suggested prompts, not automatic tasks. Answers depend on the data last synced into Heartily; grades are available only when your source provides them.</p>
          </div>
        </section>

        <section className="ai-section" id="connect" aria-labelledby="connect-title">
          <div className="ai-container">
            <p className="ai-eyebrow">One account. Your choice of assistant.</p>
            <h2 id="connect-title">Make the connection.</h2>
            <p className="ai-section-intro">First, add your courses or connect an LMS in Heartily. Parents should link a student. Then choose your assistant below.</p>
            <div className="ai-connection-layout">
              <div className="ai-provider-selector" role="group" aria-label="Choose your assistant">{connectionGuides.map((item) => <button key={item.id} type="button" aria-pressed={selected === item.id} onClick={() => setSelected(item.id)}>{item.name}</button>)}</div>
              <article className="ai-connection-panel" aria-labelledby="provider-title">
                <p className="ai-eyebrow">{guide.label}</p>
                <h3 id="provider-title">Connect with {guide.name}</h3>
                <p>{guide.intro}</p>
                <div className="ai-server-box">
                  <label htmlFor="ai-server-address">Heartily server address</label>
                  <div className="ai-server-controls"><input id="ai-server-address" name="server_address" readOnly value={serverUrl} onFocus={(event) => event.target.select()} /><button type="button" className="ai-button ai-button-secondary" onClick={copyAddress}>Copy address</button></div>
                  <p role="status" className="ai-copy-status">{copyStatus}</p>
                  {isLocal && <p className="ai-muted">Local preview: cloud assistants need the public HTTPS address of a deployed Heartily server.</p>}
                </div>
                <ol className="ai-steps">{guide.steps.map((step) => <li key={step}>{step.replace("{serverUrl}", serverUrl)}</li>)}</ol>
                {guide.note && <p className="ai-note">{guide.note}</p>}
                <p className="ai-source"><a href={guide.source} target="_blank" rel="noopener noreferrer">Official {guide.name} setup documentation ↗</a></p>
                <p className="ai-muted">Setup references checked September 9, 2026. Provider menus and account availability can change. These instructions are based on provider documentation; a live Heartily connection in each service has not yet been verified.</p>
              </article>
            </div>
          </div>
        </section>

        <section className="ai-section ai-white" aria-labelledby="access-title">
          <div className="ai-container">
            <h2 id="access-title">Your account. Your permission.</h2>
            <dl className="ai-example-grid">
              <div><dt>Only the school data you can access</dt><dd>Students share their own visible, active courses and assignments. Parents share only currently linked students. Removing a family link stops that student’s access on the next request.</dd></div>
              <div><dt>Read-only, by design</dt><dd>Assistants can read names, courses, due dates, assignment status, available grades and manual completion state. They cannot edit or submit work, change grades, trigger a sync, or retrieve LMS credentials.</dd></div>
              <div><dt>Sign in on Heartily</dt><dd>OAuth opens a Heartily sign-in and consent screen. Check the account before approving. Your assistant receives a scoped connection, not your Heartily password.</dd></div>
              <div><dt>Disconnect whenever you want</dt><dd>Use Settings → AI assistants → Manage connected apps. Revoking access stops future requests; it does not erase information already included in assistant conversations.</dd></div>
            </dl>
            <p className="ai-muted">The assistant provider processes the school data you choose to share under its own policies. Review the consent screen and use an account appropriate for your family or school.</p>
          </div>
        </section>

        <section className="ai-section" aria-labelledby="help-title">
          <div className="ai-container">
            <h2 id="help-title">A little help getting started.</h2>
            <div className="ai-faq">
              <details><summary>What is MCP?</summary><p>Model Context Protocol is a standard way for an AI assistant to ask another app for information. Heartily’s server gives supported assistants four read-only tools: list students, list courses, list assignments, and read one assignment.</p></details>
              <details><summary>Why can’t I see the option to add Heartily?</summary><p>Check whether your assistant account allows custom remote MCP connections. Some plans need developer mode or an administrator’s approval. Heartily is a custom connection, not a verified listing in each provider’s app directory.</p></details>
              <details><summary>Why is a course, assignment or grade missing?</summary><p>Sync the source in Heartily first. Hidden or inactive courses are excluded. Parents need an active student link. Calendar feeds may not include grades or submission status, and long assignment lists may require your assistant to request another page.</p></details>
              <details><summary>Can it do my homework or send reminders automatically?</summary><p>This connection reads organization and progress data; it does not submit homework or change assignments. You can ask for a study plan. Recurring check-ins require a scheduling feature in your assistant, configured separately.</p></details>
              <details><summary>What if sign-in or the connection fails?</summary><p>Use the exact server address, choose OAuth, and allow automatic client registration when offered. Enable Heartily in the conversation after connecting. If access expired or was revoked, connect again. A URL pasted into a chat alone does not authorize access.</p></details>
            </div>
            <p className="ai-help-link">Still need a hand? <Link to="/support">Contact Heartily support</Link>.</p>
          </div>
        </section>
      </main>
      <footer className="ai-guide-footer"><div className="ai-container"><Link to="/welcome">Heartily</Link><Link to="/legal/privacy">Privacy policy</Link>{isAuthenticated ? <Link to="/settings">Settings</Link> : <a href="/users/sign_in">Sign in</a>}</div></footer>
    </div>
  )
}
